import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Backdrop, CircularProgress, Snackbar } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ImagePlus, Trash2 } from "lucide-react";
import { getDetalleEvento, postAddEvento, postUpdateEvento } from "../../../api/ApiService";
import HeaderCustom from "../../../components/HeaderCustom";

export default function EventoAddEditPage() {
    const { id } = useParams();
    const stateToken = useSelector((state) => state.reducerGlobal.token);

    const navigate = useNavigate();

    const [loadingPage, setLoadingPage] = useState(Boolean(id));
    const [loadingGuardar, setLoadingGuardar] = useState(false);

    const [open, setOpen] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [mensaje, setMensaje] = useState("");

    const [formData, setFormData] = useState({
        titulo: "",
        ubicacion: "",
        descripcion: "",
        vigencia_desde: null,
        vigencia_hasta: null,
        fecha_evento_desde: null,
        fecha_evento_hasta: null,
        imagenPreview: "",
        imagenFile: null,
    });

    const openAlert = (type, text) => {
        setSeverity(type);
        setMensaje(text);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const toInputDateTime = (value) => {
        if (!value) return null;
        const date = dayjs(value);
        return date.isValid() ? date : null;
    };

    const toBackendDateTime = (value) => {
        if (!value) return "";
        return dayjs(value).format("YYYY/MM/DD HH:mm:ss");
    };

    useEffect(() => {
        if (!id) {
            setLoadingPage(false);
            return;
        }

        if (!stateToken?.access_token) return;

        setLoadingPage(true);

        getDetalleEvento(id, stateToken.access_token)
            .then((response) => {
                const data = response?.data || {};

                setFormData((prev) => ({
                    ...prev,
                    titulo: data.titulo || "",
                    ubicacion: data.ubicacion || "",
                    descripcion: data.descripcion || "",
                    vigencia_desde: toInputDateTime(data.vigencia_desde),
                    vigencia_hasta: toInputDateTime(data.vigencia_hasta),
                    fecha_evento_desde: toInputDateTime(data.fecha_evento_desde),
                    fecha_evento_hasta: toInputDateTime(data.fecha_evento_hasta),
                    imagenPreview: data.imagen || "",
                    imagenFile: null,
                }));
            })
            .catch(() => {
                openAlert("error", "Error al cargar los datos del evento.");
            })
            .finally(() => {
                setLoadingPage(false);
            });
    }, [id, stateToken?.access_token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAgregarImagen = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result;
            if (!result) return;

            const img = new Image();
            img.onload = () => {
                if (img.width === 720 && img.height === 720) {
                    setFormData((prev) => ({
                        ...prev,
                        imagenPreview: result,
                        imagenFile: file,
                    }));
                } else {
                    openAlert("error", "La imagen debe tener dimensiones 720x720 px.");
                }
            };
            img.src = result;
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setFormData((prev) => ({
            ...prev,
            imagenPreview: "",
            imagenFile: null,
        }));

        const fileInput = document.getElementById("imagen_evento");
        if (fileInput) fileInput.value = "";
    };

    const validateForm = () => {
        const {
            titulo,
            ubicacion,
            descripcion,
            imagenPreview,
            vigencia_desde,
            vigencia_hasta,
            fecha_evento_desde,
            fecha_evento_hasta,
        } = formData;

        if (!titulo.trim() || !ubicacion.trim() || !descripcion.trim() || !imagenPreview) {
            return "Todos los campos son obligatorios.";
        }

        if (!vigencia_desde || !vigencia_hasta || !fecha_evento_desde || !fecha_evento_hasta) {
            return "Las fechas son obligatorias.";
        }

        if (!dayjs(vigencia_desde).isValid() || !dayjs(vigencia_hasta).isValid() ||
            !dayjs(fecha_evento_desde).isValid() || !dayjs(fecha_evento_hasta).isValid()) {
            return "Alguna fecha no es válida.";
        }

        if (dayjs(vigencia_desde).isAfter(dayjs(vigencia_hasta))) {
            return "La fecha de fin de vigencia no puede ser menor a la fecha de inicio.";
        }

        if (dayjs(fecha_evento_desde).isAfter(dayjs(fecha_evento_hasta))) {
            return "La fecha de fin del evento no puede ser menor a la fecha de inicio.";
        }

        return "";
    };








    const buildPayload = () => {
        const payload = new FormData();
        payload.append("titulo", formData.titulo);
        payload.append("descripcion", formData.descripcion);
        payload.append("ubicacion", formData.ubicacion);
        payload.append("vigencia_desde", toBackendDateTime(formData.vigencia_desde));
        payload.append("vigencia_hasta", toBackendDateTime(formData.vigencia_hasta));
        payload.append("fecha_evento_desde", toBackendDateTime(formData.fecha_evento_desde));
        payload.append("fecha_evento_hasta", toBackendDateTime(formData.fecha_evento_hasta));

        if (formData.imagenFile) {
            payload.append("imagen", formData.imagenFile);
        }

        //poner en consola lo que se va a enviar al backend, para verificar que el formato de las fechas sea correcto y que la imagen se haya agregado correctamente
        console.log("Payload a enviar:");
        for (let pair of payload.entries()) {
            console.log(`${pair[0]}: ${pair[1]}`);
        }

        return payload;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            openAlert("warning", validationError);
            return;
        }

        if (!stateToken?.access_token) {
            openAlert("error", "No se encontró token de autenticación.");
            return;
        }

        const payload = buildPayload();
        console.log("payload", payload);
        setLoadingGuardar(true);

        const request = id
            ? postUpdateEvento(payload, id, stateToken.access_token)
            : postAddEvento(payload, stateToken.access_token);

        request
            .then(() => {
                openAlert("success", id ? "Evento actualizado con éxito." : "Evento creado con éxito.");
                setTimeout(() => {
                    navigate('/app/admin/eventos');
                }, 1500);
            })
            .catch(() => {
                openAlert("error", id ? "Error al actualizar el evento." : "Error al guardar el evento.");
            })
            .finally(() => {
                setLoadingGuardar(false);
            });
    };

    const handleCancelar = () => {
        window.history.back();
    };

    return (
        <HeaderCustom>
            <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
                {/* <div className="max-w-5xl mx-auto mt-20"> */}
                <div className="max-w-5xl mx-auto">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800 mb-1">{id ? "Editar Evento" : "Nuevo Evento"}</h1>
                        {/* <p className="text-sm text-gray-500">
                            {id
                                ? "Modifica los detalles del evento. Asegúrate de que toda la información esté actualizada."
                                : "Completa el formulario para crear un nuevo evento. Todos los campos son obligatorios."}
                        </p> */}
                    </div>

                    {loadingPage ? (
                        <div className="bg-white rounded-xl shadow-md p-8">
                            <div className="h-6 bg-gray-200 rounded w-1/3 mb-6 animate-pulse" />
                            <div className="space-y-4">
                                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                                <div className="h-24 bg-gray-200 rounded animate-pulse" />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <form onSubmit={handleSubmit}>
                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                                                <input
                                                    type="text"
                                                    name="titulo"
                                                    value={formData.titulo}
                                                    onChange={handleInputChange}
                                                    placeholder="Ej: Noche de Jazz"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
                                                <input
                                                    type="text"
                                                    name="ubicacion"
                                                    value={formData.ubicacion}
                                                    onChange={handleInputChange}
                                                    placeholder="Ej: Plaza Central"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                                                <textarea
                                                    name="descripcion"
                                                    value={formData.descripcion}
                                                    onChange={handleInputChange}
                                                    rows={5}
                                                    placeholder="Describe el evento"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Vigencia desde</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_desde}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_desde: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    ampm={false}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Vigencia hasta</label>
                                                <DateTimePicker
                                                    value={formData.vigencia_hasta}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, vigencia_hasta: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    ampm={false}
                                                    minDateTime={formData.vigencia_desde || undefined}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha evento desde</label>
                                                <DateTimePicker
                                                    value={formData.fecha_evento_desde}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, fecha_evento_desde: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    ampm={false}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha evento hasta</label>
                                                <DateTimePicker
                                                    value={formData.fecha_evento_hasta}
                                                    onChange={(newValue) => setFormData((prev) => ({ ...prev, fecha_evento_hasta: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    ampm={false}
                                                    minDateTime={formData.fecha_evento_desde || undefined}
                                                    slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="px-6 pb-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Imagen (720x720)</label>
                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                                            <input
                                                id="imagen_evento"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleAgregarImagen}
                                            />

                                            {!formData.imagenPreview ? (
                                                <label htmlFor="imagen_evento" className="cursor-pointer flex flex-col items-center text-center">
                                                    <ImagePlus className="w-8 h-8 text-gray-400 mb-2" />
                                                    <p className="text-sm text-gray-600">Seleccionar imagen</p>
                                                    <p className="text-xs text-gray-500 mt-1">PNG o JPG con tamaño 720x720</p>
                                                </label>
                                            ) : (
                                                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                                    <img
                                                        src={formData.imagenPreview}
                                                        alt="Vista previa"
                                                        className="w-36 h-36 rounded-md object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveImage}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Quitar imagen
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-start gap-3 p-6 border-t border-gray-200">
                                        <button
                                            type="submit"
                                            disabled={loadingGuardar}
                                            className="px-6 py-2 bg-[#104063] hover:bg-[#0d3553] disabled:opacity-70 text-white rounded-lg font-medium transition-colors"
                                        >
                                            {loadingGuardar ? "Guardando..." : id ? "Guardar Cambios" : "Guardar"}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleCancelar}
                                            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                        >
                                            Cancelar
                                        </button>

                                    </div>
                                </form>
                            </LocalizationProvider>
                        </div>
                    )}
                </div>

                <Backdrop open={loadingGuardar} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    open={open}
                    autoHideDuration={2200}
                    onClose={handleClose}
                >
                    <Alert onClose={handleClose} severity={severity} elevation={6} variant="filled">
                        {mensaje}
                    </Alert>
                </Snackbar>
            </div>

        </HeaderCustom>

    );
}
